import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { metaContent } from '@/utils';
import JsonLD from '../JsonLD/JsonLD';
import { useEffect } from 'react';

const SEO = ({ pageName, children, customTitle, customDescription }) => {
    // Default to home if pageName is not found in metaContent
    const pageData = metaContent[pageName] || metaContent.home;

    const title = customTitle || pageData.title;
    const description = customDescription || pageData.description;
    const keywords = pageData.keywords || [];
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    // Create keywords string for internal use
    const keywordsString = keywords.join(', ');

    // Default Open Graph image
    const ogImage = `${typeof window !== 'undefined' ? window.location.origin : 'https://grayquest.com'}/assets/images/og-image.jpg`;

    useEffect(() => {
        if (title) {
            document.title = title;
        }
    }, [title]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name='description' content={description} />
                
                {/* Open Graph Tags */}
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:type' content='website' />
                <meta property='og:url' content={currentUrl} />
                <meta property='og:image' content={ogImage} />
                <meta property='og:image:width' content='1200' />
                <meta property='og:image:height' content='630' />
                <meta property='og:site_name' content='GrayQuest' />
                <meta property='og:locale' content='en_IN' />

                {/* Keywords for SEO analysis and content optimization */}
                {keywords.length > 0 && <meta name='keywords' content={keywordsString} data-purpose='seo-analysis' />}
                {/* Primary keyword for content focus */}
                {keywords.length > 0 && (
                    <meta name='primary-keyword' content={keywords[0]} data-purpose='content-focus' />
                )}

                {/* Twitter Card Tags */}
                <meta name='twitter:card' content='summary_large_image' />
                <meta name='twitter:title' content={title} />
                <meta name='twitter:description' content={description} />
                <meta name='twitter:image' content={ogImage} />
                <meta name='twitter:site' content='@gqfinance' />
                <meta name='twitter:creator' content='@gqfinance' />

                {/* Additional SEO Meta Tags */}
                <meta name='author' content='GrayQuest' />
                <meta name='publisher' content='GrayQuest' />
                <meta name='application-name' content='GrayQuest' />
                <meta name='theme-color' content='#3e52ce' />

                {/* Canonical URL */}
                <link rel='canonical' href={currentUrl} />

                {/* Alternate language versions */}
                <link rel='alternate' hrefLang='en-IN' href={currentUrl} />
                <link rel='alternate' hrefLang='x-default' href={currentUrl} />
            </Helmet>
            <JsonLD pageName={pageName} />
            {children}
        </>
    );
};

SEO.propTypes = {
    pageName: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    customTitle: PropTypes.string,
    customDescription: PropTypes.string,
};

export default SEO;